/* Utils */
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '@/features/auth';
import { pageNames } from './constants';
/* Components */
import { BreadCrumbs } from './BreadCrumbs';
import { NotificationButton } from '@/shared/components/Buttons/NotificationButton.tsx/NotificationButton';
/* Types */
import type { NavbarProps } from './Navbar.types';
/* Assets */
import { ChevronDown, LogOut, Menu, Settings, User, X } from 'lucide-react';

export const Navbar = ({ setSidebarOpen, sidebarOpen }: NavbarProps) => {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userName = user ? `${user.first_name} ${user.last_name}` : '';

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const currentPage = pathSegments[pathSegments.length - 1] || 'dashboard';
  const pageName =
    pageNames[currentPage] || currentPage.charAt(0).toUpperCase() + currentPage.slice(1);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout({ redirectTo: '/auth/login' });
  };

  if (!user) return null;

  const basePath = '';

  return (
    <header className='sticky top-0 z-40 h-16 border-b border-gray-200 bg-white'>
      <div className='flex h-full items-center justify-between px-3 sm:px-4 lg:px-6'>
        {/* Left side - Hamburger & Breadcrumbs */}
        <div className='flex items-center gap-2 sm:gap-3'>
          {/* Mobile menu toggle */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={clsx(
              'flex h-9 w-9 items-center justify-center rounded-lg transition-all duration-200 lg:hidden',
              'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            )}
          >
            {sidebarOpen ? <X className='h-5 w-5' /> : <Menu className='h-5 w-5' />}
          </button>

          {/* Breadcrumbs */}
          <BreadCrumbs pageName={pageName} basePath={basePath} />
        </div>

        {/* Right side - Actions & User */}
        <div className='flex items-center gap-1 sm:gap-2'>
          {/* Notifications */}
          <NotificationButton />

          {/* Divider - hidden on mobile */}
          <div className='mx-1 hidden h-6 w-px bg-gray-200 sm:block' />

          {/* User Dropdown */}
          <div className='relative' ref={dropdownRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className={clsx(
                'flex items-center gap-2 rounded-lg p-1.5 transition-all duration-200 hover:cursor-pointer',
                'hover:bg-gray-100',
                userMenuOpen && 'bg-gray-100'
              )}
            >
              {/* Avatar */}
              <div
                className={clsx(
                  'flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg shadow-md',
                  (!user.avatar || avatarError) && 'bg-blue-600 text-white shadow-blue-200',
                  user.avatar && !avatarError && 'text-gray-600'
                )}
              >
                {user.avatar && !avatarError ? (
                  <img
                    src={user.avatar}
                    alt={userName}
                    className='h-full w-full object-cover'
                    onError={() => setAvatarError(true)}
                  />
                ) : (
                  <span className='text-sm font-semibold'>{userName.charAt(0).toUpperCase()}</span>
                )}
              </div>

              {/* User info - hidden on mobile */}
              <div className='hidden max-w-32 text-left md:block'>
                <span className='block truncate text-sm font-medium text-gray-900'>{userName}</span>
                <span className='block text-xs text-gray-500'>Supplier</span>
              </div>

              <ChevronDown
                className={clsx(
                  'hidden h-4 w-4 text-gray-400 transition-transform duration-200 sm:block',
                  userMenuOpen && 'rotate-180'
                )}
              />
            </button>

            {/* Dropdown Menu */}
            {userMenuOpen && (
              <div className='absolute right-0 mt-2 w-56 origin-top-right rounded-lg border border-gray-200 bg-white shadow-lg sm:w-64'>
                {/* User Info Header */}
                <div className='border-b border-gray-100 p-3'>
                  <div className='flex items-center gap-3'>
                    <div
                      className={clsx(
                        'flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg shadow-md',
                        (!user.avatar || avatarError) && 'bg-blue-600 text-white shadow-blue-200',
                        user.avatar && !avatarError && 'text-gray-600'
                      )}
                    >
                      {user.avatar && !avatarError ? (
                        <img
                          src={user.avatar}
                          alt={userName}
                          className='h-full w-full object-cover'
                          onError={() => setAvatarError(true)}
                        />
                      ) : (
                        <span className='text-base font-semibold'>
                          {userName.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className='min-w-0 flex-1'>
                      <p className='truncate text-sm font-medium text-gray-900'>{userName}</p>
                      <p className='truncate text-xs text-gray-500'>{user.email}</p>
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className='p-1.5'>
                  <Link
                    to={`${basePath}/profile`}
                    onClick={() => setUserMenuOpen(false)}
                    className='flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-gray-600 transition-all duration-200 hover:bg-gray-100 hover:text-gray-900'
                  >
                    <User className='h-4 w-4 text-gray-500' />
                    My Profile
                  </Link>
                  <Link
                    to={`${basePath}/settings`}
                    onClick={() => setUserMenuOpen(false)}
                    className='flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-gray-600 transition-all duration-200 hover:bg-gray-100 hover:text-gray-900'
                  >
                    <Settings className='h-4 w-4 text-gray-500' />
                    Settings
                  </Link>
                </div>

                {/* Divider */}
                <div className='border-t border-gray-100' />

                {/* Sign Out */}
                <div className='p-1.5'>
                  <button
                    onClick={handleLogout}
                    className='flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-red-600 transition-all duration-200 hover:cursor-pointer hover:bg-red-50'
                  >
                    <LogOut className='h-4 w-4' />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
